import { JSX, useCallback, useState } from 'react';
import type { IDeparture } from '../../../../models/departure.model';
import { TranslationService } from '../../../../services/translation.service';
import { useTexts } from '../../../../hooks/texts.hook';
import './style.scss';
import type { IDeparturesBoardItem } from './models/departure-board-item.model';
import useInterval from './hooks/use-interval.hook';
import { ApiPlaneraResaService } from '../../../../services/api-planera-resa/api-planera-resa.service';
import { ErrorResponseHelper } from '../../../../helpers/error-response.helper';
import type { ILocation } from '../../../../models/location';
import WheelchairIcon from '../../../Shared/WheelchairIcon';
import Spinner from '../../../Shared/Spinner';
import type { IErrorResponse } from '../../../../models/error-response.model';
import { ErrorHandlerHelper } from '../../../../helpers/error-handler.helper';
import { WheelchairIconType } from '../../../Shared/WheelchairIcon/wheelchair-icon-type.enum';
import LineBadge from '../../../Shared/LineBadge';
import LogService from '../../../../services/log-service/log.service';
import { DateTimeHelper } from '../../../../helpers/date-time.helper';
import { DateTime } from 'luxon';
import { StopPointType } from '../../../../models/stop-point-type.enum';

function departsAtOrAfter(departure: IDeparture, time: DateTime): boolean {
  if (departure.estimatedTime) {
    return departure.estimatedTime >= time;
  }
  return departure.plannedTime >= time;
}

function filterAndGroupDepartures(departures: IDeparture[], minimumDepartureTime: DateTime): IDeparturesBoardItem[] {
  const grouped: IDeparturesBoardItem[] = [];

  const filteredDepartures = departures.filter((d) => departsAtOrAfter(d, minimumDepartureTime));

  filteredDepartures.forEach((departure) => {
    let added = false;

    for (const groupElement of grouped) {
      if (
        groupElement.nextDeparture.serviceJourney.line?.shortName === departure.serviceJourney.line?.shortName &&
        groupElement.nextDeparture.serviceJourney.direction === departure.serviceJourney.direction
      ) {
        groupElement.thereAfterDeparture = departure;
        added = true;
        break;
      }
    }
    if (!added) {
      grouped.push({ nextDeparture: departure, thereAfterDeparture: undefined });
    }
  });

  return grouped;
}

function DepartureBoard({ stopArea }: { stopArea: ILocation }) {
  const { t } = useTexts();
  const [errorResponse, setErrorResponse] = useState<IErrorResponse | undefined>(undefined);
  const [departures] = useInterval<IDeparture[]>({
    callback: useCallback(async () => {
      try {
        const result = stopArea.gid ? await ApiPlaneraResaService.getDepartures(stopArea.gid, 200) : [];
        setErrorResponse(undefined);
        return result;
      } catch (error: unknown) {
        if (ErrorHandlerHelper.isErrorResponse(error)) {
          setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
        }
        if (ErrorHandlerHelper.isAprErrorSignificant(error)) {
          LogService.logError(error, 'Error occurred while fetching departures in DepartureBoard');
        }
        return [];
      }
    }, [stopArea]),
    delay: 15000,
    dependency: stopArea,
  });
  const groupedDepartures = departures ? filterAndGroupDepartures(departures, DateTime.now()) : [];

  function getMinutesOrTextTo(departure: IDeparture): string {
    if (departure.isCancelled) {
      return TranslationService.get('departureboard.cancelled');
    }

    const departureTime = departure.estimatedTime || departure.plannedTime;

    const now = DateTime.now();
    const diffMinutes = DateTimeHelper.minutes(now, departureTime);

    if (diffMinutes > 59) {
      return DateTimeHelper.toTime(departureTime);
    }
    return diffMinutes <= 0 ? TranslationService.get('common.now') : `${diffMinutes}`;
  }

  function renderThereafter(departure: IDeparture | undefined): JSX.Element {
    if (!departure) {
      return <></>;
    }
    return (
      <>
        {!departure.isCancelled && departure.serviceJourney.line?.isWheelchairAccessible && (
          <span className="departure-board__wheel-chair-icon d-inline-block me-1">
            <WheelchairIcon iconType={WheelchairIconType.white} sizeInRem={1.25} />
          </span>
        )}
        {departures?.length && <span>{departures.length > 1 ? getMinutesOrTextTo(departure) : ''}</span>}
      </>
    );
  }

  function getPlatFormTrackTranslation(): JSX.Element  {
    let trainStopCount = 0;
    let otherCount = 0;

    departures?.forEach((departure) => {
      if (departure.stopPointType === StopPointType.trainStop) {
        trainStopCount++;
      } else {
        otherCount++;
      }
    });

    if (trainStopCount > 0 && otherCount === 0) return <>{t.track}</>;

    if (trainStopCount > 0 && otherCount > 0) return <><span>{t.platform}/</span><br className="d-sm-none" /><span>{t.track}</span></>;

    return <>{t.platform}</>;
  }

  return (
    <div className="departure-board h-100">
      {departures !== undefined && departures.length > 0 && (
        <table aria-live="polite" className="text-color-white w-100 departure-board__table">
          <thead>
            <tr>
              <th colSpan={2} className="pb-1 fw-normal" scope="colgroup">
                {TranslationService.get('departureboard.line')}
              </th>
              <th className="fw-normal text-nowrap text-end" scope="col">
                {TranslationService.get('departureboard.next')} (min)
              </th>
              <th className="d-none d-sm-table-cell ps-2 fw-normal text-end" scope="col">
                {TranslationService.get('departureboard.thereafter')}
              </th>
              <th className="ps-2 fw-normal text-end" scope="col">
                {getPlatFormTrackTranslation()}
              </th>
            </tr>
          </thead>
          <tbody>
            {groupedDepartures.map((departure) => (
              <tr key={departure.nextDeparture.serviceJourney.gid}>
                <td className="py-3">
                  {departure.nextDeparture.serviceJourney.line && (
                    <div className="d-flex">
                      <LineBadge line={departure.nextDeparture.serviceJourney.line} useShortNameAndNumberForTrain={true} className="flex-grow-1" />
                    </div>
                  )}
                </td>
                <td className="ps-2 departure-board__direction-cell">
                  <span className="departure-board__direction-text">
                    <span className="text-break">{departure.nextDeparture.serviceJourney.direction?.replace(/,?\s*Påstigning fram$/, '')}</span>
                    {departure.nextDeparture.serviceJourney.via && departure.nextDeparture.serviceJourney.via.length > 0 && (
                      <em className="small">
                        <br />
                        {departure.nextDeparture.serviceJourney.via}
                      </em>
                    )}
                  </span>
                </td>
                <td className="ps-2 text-end">
                  {!departure.nextDeparture.isCancelled && departure.nextDeparture.serviceJourney.line?.isWheelchairAccessible && (
                    <span className="departure-board__wheel-chair-icon d-inline-block me-1">
                      <WheelchairIcon iconType={WheelchairIconType.white} sizeInRem={1.25} />
                    </span>
                  )}
                  <span className="fw-bold">{getMinutesOrTextTo(departure.nextDeparture)}</span>
                </td>
                <td className="ps-2 d-none d-sm-table-cell text-end">{renderThereafter(departure.thereAfterDeparture)}</td>
                <td className="ps-2 fw-bold text-end">{departure.nextDeparture.platform}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(departures === undefined || departures.length === 0) && (
        <div className="h-100 d-flex align-items-center justify-content-center text-white">
          {departures !== undefined && departures.length === 0 && !errorResponse && <span className="text-center px-2">{TranslationService.get('departureboard.nodata')}</span>}
          {departures === undefined && <Spinner delayInMs={100} />}
          {errorResponse !== undefined && (errorResponse.statusCode === 503 || errorResponse.statusCode === 500) && (
            <span className="text-center px-2">{errorResponse?.errorMessage || t.generalError}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default DepartureBoard;
